import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { GetCountry, GetProvince } from "api/rpc/2024-04/country";
import {
  GetTeeOnPaymentAccounts,
  PutTeeOnPaymentAccounts,
} from "api/rpc/2024-04/facilityAdmin/payment/teeonPayments/accounts";

import { showError } from "redux/actions/ui";
import { ICountry, IProvince } from "redux/reducers/models/customer";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { Select } from "components/select";
import Spin from "components/spin/spin";
import { isEqualWith, isNull } from "lodash";
import { postcodeValidator, postcodeValidatorExistsForCountry } from "postcode-validator";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
// import { unsavedChangesExist } from "helpers/Helpers";

interface ICompanyInformationState {
  company_name: string;
  email: string;
  company_address_line_1: string;
  company_city: string;
  company_country_code: string;
  company_postal: string;
  company_province_name: string;
  company_phone: string;
  company_registration_number: string;
  company_directors_provided: string;
  company_executives_provided: string;
  company_owners_provided: string;
}

interface IBusinessProfileState {
  business_profile_name: string;
  business_profile_mcc: any;
  business_profile_product_description: string;
  business_profile_support_email: string;
  business_profile_support_phone: string;
  business_profile_support_url: string;
  business_profile_url: string;
  // business_profile_privacy_policy_url: string;
}

export default function TeeOnPaymentsEdit() {
  const { t } = useTranslation();
  const { Option } = Select;

  const dispatch = useAppDispatch();

  const business_types = [
    {
      title: t("Sports Clubs / Fields - 7941"),
      value: 7941,
    },
    {
      title: t("Computer Software - 5734"),
      value: 5734,
    },
    {
      title: t("Hotels, Motels, and Resorts - 7011"),
      value: 7011,
    },
  ];

  const helpToolTip = `Must exactly match the name associated with the
  connected account's business number (BN).`;

  const [countries, setCountries] = useState<ICountry[]>([]);
  const [provinces, setProvinces] = useState<IProvince[]>([]);
  const [teeOnPaymentLoaded, setTeeOnPaymentLoaded] = useState<boolean>(false);

  const [businessInformationBeforeChanges, setBusinessInformationBeforeChanges] =
    useState<IBusinessProfileState>(undefined);

  const [companyInformationBeforeChanges, setCompanyInformationBeforeChanges] =
    useState<ICompanyInformationState>(undefined);

  const [companyInformation, setCompanyInformation] = useState<ICompanyInformationState>({
    company_name: "",
    email: "",
    company_address_line_1: "",
    company_city: "",
    company_country_code: "",
    company_province_name: "",
    company_postal: "",
    company_phone: "",
    company_registration_number: "",
    company_directors_provided: "",
    company_executives_provided: "",
    company_owners_provided: "",
  });

  const [businessInformation, setBusinessInformation] = useState<IBusinessProfileState>({
    business_profile_name: "",
    business_profile_mcc: "",
    business_profile_product_description: "",
    business_profile_support_email: "",
    business_profile_support_phone: "",
    business_profile_support_url: "",
    business_profile_url: "",
    // business_profile_privacy_policy_url: "",
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    // void loadCountries(source.token);
    void loadTeeOnPaymentsAccounts(source.token);

    return () => source.cancel();
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (companyInformation.company_country_code) {
      if (countries) {
        const country = countries?.find(country => country.code === companyInformation.company_country_code);
        void loadProvinces(country?.id, source.token);
      }
    } else {
      setProvinces([]);
    }
  }, [companyInformation.company_country_code]);

  async function loadTeeOnPaymentsAccounts(token?: CancelToken) {
    const res = await GetTeeOnPaymentAccounts(null, true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading payment integration information.")));
    }

    const countryRes = await GetCountry(null, true, token);
    if (token && token.reason) {
      return;
    }
    if (countryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading countries.")));
    }

    setCountries(countryRes.data);
    setCompanyInformation(prevState => ({
      ...prevState,
      company_name: res.data?.company_name,
      email: res.data?.email,
      company_address_line_1: res.data?.company_address_line_1,
      company_city: res.data?.company_city,
      company_country_code: res.data.country_code,
      company_province_name: res.data?.company_province_name,
      company_postal: res.data?.company_postal,
      company_phone: res.data?.company_phone,
      company_registration_number: res.data?.company_registration_number,
      company_directors_provided: res.data?.company_directors_provided,
      company_executives_provided: res.data?.company_executives_provided,
      company_owners_provided: res.data?.company_owners_provided,
    }));

    setBusinessInformation(prevState => ({
      ...prevState,
      business_profile_name: res.data?.business_profile_name,
      business_profile_mcc: Number(res.data?.business_profile_mcc),
      business_profile_product_description: res.data?.business_profile_product_description,
      business_profile_support_email: res.data?.business_profile_support_email,
      business_profile_support_phone: res.data?.business_profile_support_phone,
      business_profile_support_url: res.data?.business_profile_support_url,
      business_profile_url: res.data?.business_profile_url,
      // business_profile_privacy_url: res.data?.business_profile_privacy_policy_url,
    }));

    setTeeOnPaymentLoaded(true);
  }

  /* Called only when country_id changes & not nullish */
  async function loadProvinces(countryId: string | number, token?: CancelToken) {
    if (provinces !== undefined) {
      setProvinces(undefined);
    }
    const res = await GetProvince({ country_id: Number(countryId) }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading provinces.")));
    }

    setProvinces(res.status !== StatusCode.OK ? [] : res.data);
  }

  function handleCompanyInputChange(event: any) {
    const { id, value } = event.target;
    setCompanyInformation(prevState => ({ ...prevState, [id]: value }));
  }

  function handleBusinessInputChange(event: any) {
    const { id, value } = event.target;
    setBusinessInformation(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDropDownChange(value: any) {
    setBusinessInformation(prevState => ({ ...prevState, business_profile_mcc: value }));
  }

  async function saveTeeOnPayment() {
    if (postcodeValidatorExistsForCountry(companyInformation.company_country_code)) {
      if (!postcodeValidator(companyInformation.company_postal, companyInformation.company_country_code)) {
        dispatch(showError(t("Incorrect postal code format")));
        return;
      }
    }

    const params = {
      company_name: companyInformation.company_name,
      email: companyInformation.email,
      company_address_line_1: companyInformation.company_address_line_1,
      company_city: companyInformation.company_city,
      company_country_code: companyInformation.company_country_code,
      company_province_name: companyInformation.company_province_name,
      company_postal: companyInformation.company_postal,
      company_phone: companyInformation.company_phone,
      company_registration_number: companyInformation.company_registration_number,
      company_directors_provided: companyInformation.company_directors_provided,
      company_executives_provided: companyInformation.company_executives_provided,
      company_owners_provided: companyInformation.company_owners_provided,
      business_profile_name: businessInformation.business_profile_name,
      business_profile_mcc: String(businessInformation.business_profile_mcc),
      business_profile_product_description: businessInformation.business_profile_product_description,
      business_profile_support_email: businessInformation.business_profile_support_email,
      business_profile_support_phone: businessInformation.business_profile_support_phone,
      business_profile_support_url: businessInformation.business_profile_support_url,
      business_profile_url: businessInformation.business_profile_url,
      // business_privacy_policy_url: businessInformation.business_profile_privacy_policy_url,
    };

    const putPaymentRes = await PutTeeOnPaymentAccounts(params, true);

    if (putPaymentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving Tee On Payment")));
      return;
    }

    setCompanyInformationBeforeChanges(companyInformation);
    setBusinessInformationBeforeChanges(businessInformation);
  }

  function unsavedChangesExist() {
    if (businessInformationBeforeChanges === undefined) {
      if (teeOnPaymentLoaded) {
        setBusinessInformationBeforeChanges(businessInformation);
      }
      return false;
    }

    if (companyInformationBeforeChanges === undefined) {
      if (teeOnPaymentLoaded) {
        setCompanyInformationBeforeChanges(companyInformation);
      }
      return false;
    }

    return (
      !isEqualWith(businessInformation, businessInformationBeforeChanges, (originalValue, newValue) => {
        if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
          return true;
        }
      }) ||
      !isEqualWith(companyInformation, companyInformationBeforeChanges, (originalValue, newValue) => {
        if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
          return true;
        }
      })
    );
  }

  function handleCountryDropdown(value: any) {
    setCompanyInformation(prev => ({
      ...prev,
      company_country_code: value,
    }));
  }

  return (
    <Page
      title={t("Edit Merchant Account")}
      breadcrumbs={[
        {
          label: "Settings",
          url: "/admin/settings",
        },
        {
          label: "Payments",
          url: "/admin/settings/payments/general",
        },
        {
          label: "Tee On Payments",
          url: "/admin/settings/payments/tee-on-payments",
        },
        {
          label: "Edit Account",
          url: "/admin/settings/payments/tee-on-payments/edit",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: () => saveTeeOnPayment(),
        onCancel: () => {
          setBusinessInformation(businessInformationBeforeChanges);
          setCompanyInformation(companyInformationBeforeChanges);
        },
      }}
    >
      <Card>
        <Card.Section
          title={t("Company Information")}
          subtitle={t("Enter in the details related to the business entity")}
        >
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={companyInformation.company_name ?? ""}
                label={t("Legal Business Name")}
                id="company_name"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={t("Name...")}
                suffix={
                  <>
                    <OverlayTrigger
                      placement="top-end"
                      overlay={
                        <Tooltip className="tee-on-payments-wide-tooltip" id="">
                          {helpToolTip}
                        </Tooltip>
                      }
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_2065_17136)">
                          <path
                            d="M6.0587 6.00065C6.21543 5.5551 6.5248 5.17939 6.932 4.94007C7.3392 4.70076 7.81796 4.61328 8.28348 4.69313C8.749 4.77297 9.17124 5.015 9.47542 5.37634C9.77959 5.73767 9.94607 6.195 9.94536 6.66732C9.94536 8.00065 7.94536 8.66732 7.94536 8.66732M7.9987 11.334H8.00536M14.6654 8.00065C14.6654 11.6825 11.6806 14.6673 7.9987 14.6673C4.3168 14.6673 1.33203 11.6825 1.33203 8.00065C1.33203 4.31875 4.3168 1.33398 7.9987 1.33398C11.6806 1.33398 14.6654 4.31875 14.6654 8.00065Z"
                            stroke="#667085"
                            strokeWidth="1.33333"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_2065_17136">
                            <rect width="16" height="16" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </OverlayTrigger>
                  </>
                }
              />

              <Input
                value={companyInformation.company_registration_number ?? ""}
                label={t("Business Registration Number")}
                id="company_registration_number"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={t("Company Registration #...")}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={companyInformation.email ?? ""}
                label={t("Contact Email")}
                id="email"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={t("Email...")}
              />

              <Input
                value={companyInformation.company_phone ?? ""}
                label={t("Phone")}
                id="company_phone"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={t("Phone...")}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={companyInformation.company_address_line_1 ?? ""}
                label={t("Address Line 1")}
                id="company_address_line_1"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={t("Address...")}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={companyInformation.company_city ?? ""}
                label={t("City")}
                id="company_city"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={t("City...")}
              />

              <Select
                label={t("Country")}
                onChange={(value: any) => handleCountryDropdown(value)}
                defaultValue={companyInformation?.company_country_code}
              >
                {countries.map((country: any) => {
                  return (
                    <Select.Option key={country.id} value={country.code} name={country.name} extraValues={country}>
                      {country.name}
                    </Select.Option>
                  );
                })}
              </Select>
            </FormLayout.Group>

            <FormLayout.Group>
              <Select
                label={t("Province")}
                onChange={(value: string) =>
                  setCompanyInformation(prev => ({
                    ...prev,
                    company_province_name: value,
                  }))
                }
                defaultValue={companyInformation?.company_province_name}
              >
                {provinces === undefined && (
                  <Select.Option value={"loading"} disabled>
                    <div style={{ height: "40px", padding: "8px 16px" }}>
                      <Spin />
                    </div>
                  </Select.Option>
                )}
                {provinces?.map(province => {
                  return (
                    <Select.Option key={province.id} value={province.name} name={province.name}>
                      {province.name}
                    </Select.Option>
                  );
                })}
              </Select>

              <Input
                value={companyInformation.company_postal ?? ""}
                label={t("Postal")}
                id="company_postal"
                onChange={e => handleCompanyInputChange(e)}
                placeholder={t("Postal Code...")}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>

        <Card.Section
          title={t("Business Profile")}
          subtitle={t("This information are details related to the business that may be customer facing")}
        >
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={businessInformation.business_profile_name ?? ""}
                label={t("Business Name")}
                id="business_profile_name"
                onChange={e => handleBusinessInputChange(e)}
                placeholder={t("Business Name...")}
                helpText={t("The operating name of the company, if it's different than the legal business name")}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={businessInformation.business_profile_url ?? ""}
                label={t("Business URL")}
                id="business_profile_url"
                onChange={e => handleBusinessInputChange(e)}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <span className="facility-edit-input_container">
                <Select
                  label={t("Merchant Code")}
                  onChange={(value: any) => handleDropDownChange(value)}
                  defaultValue={Number(businessInformation.business_profile_mcc)}
                >
                  {business_types.map((business: any, index: number) => {
                    return (
                      <Option key={index} value={business.value} name={business.title}>
                        {business.title}
                      </Option>
                    );
                  })}
                </Select>
              </span>
            </FormLayout.Group>

            <FormLayout.Group>
              <Input
                value={businessInformation.business_profile_support_email ?? ""}
                label={t("Support Email")}
                id="business_profile_support_email"
                onChange={e => handleBusinessInputChange(e)}
              />
              <Input
                value={businessInformation.business_profile_support_phone ?? ""}
                label={t("Support Phone")}
                id="business_profile_support_phone"
                onChange={e => handleBusinessInputChange(e)}
              />
              <Input
                value={businessInformation.business_profile_support_url ?? ""}
                label={t("Support URL")}
                id="business_profile_support_url"
                onChange={e => handleBusinessInputChange(e)}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={businessInformation.business_profile_product_description ?? ""}
                label={t("Product Decription")}
                id="business_profile_product_description"
                onChange={e => handleBusinessInputChange(e)}
                placeholder={t("Product Description...")}
                type={"text"}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}
